import { useEffect, useMemo, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA } from "@/lib/prava";
import { Calculator, Target } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useCielovaCena, useCenaNahlad } from "@/api/hooks/useVarianty";
import { useUlozMarze, type MarzaZostavy } from "@/api/hooks/useMarze";
import { useDebounced } from "@/lib/useDebounced";
import { formatCisloBezNul, formatEur, formatPct, normalizeNum, parseDecimal } from "@/lib/format";
import type {
  CielovaCenaVysledok,
  Varianta,
  Zostava,
  ZostavaMarzaRow,
} from "@/api/schemas";

/**
 * Pult na marže — všetky cenové páky výpočtu na jednom mieste (KOLO 65).
 *
 * Do KOLA 64 to bola kalkulačka „Cieľová cena": zadáš cenu, backend
 * dopočíta globálny zisk a Použiť ho uloží. Bola to čierna skrinka
 * s jedným číslom — user nevidel, z čoho sa cena skladá, a keď vyšla
 * nedosiahnuteľná (napr. preto, že časť zostáv má vlastnú maržu), nemal
 * kde zasiahnuť.
 *
 * `varianta.zisk_pct` je **globál na celý výpočet**; zostava ho môže prebiť
 * vlastným `zisk_pct_override`. Prázdne pole v tabuľke = zdedený globál.
 *
 * Čísla počíta **backend** (`POST /varianty/{id}/cena-nahlad`) tým istým
 * kalkulátorom ako reálnu cenu, takže čo pult ukáže, to po uložení naozaj
 * bude. Prepis vzorca v TS by sa rozišiel o centy (pravidlo z KOLA 62b).
 *
 * Ukladá sa **až na „Uložiť"** — dá sa teda skúšať bez prepísania reálnej
 * ceny a zavrieť bez následku.
 */
export function CenyAMarzeDialog({
  open,
  onOpenChange,
  zakazkaId,
  varianta,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  zakazkaId: string;
  varianta: Varianta;
}): JSX.Element {
  const zostavy = (varianta.zostavy ?? []) as Zostava[];
  // Zľavu varianty (podmienka ponuky) mení obchodník (KOLO 231)
  const prava = usePrava();

  const [zisk, setZisk] = useState("");
  const [sleva, setSleva] = useState("");
  /** `zostava_id → text v poli`; prázdny string = zdediť globál. */
  const [marze, setMarze] = useState<Record<string, string>>({});

  const [cena, setCena] = useState("");
  const [sDph, setSDph] = useState(true);
  const [ciel, setCiel] = useState<CielovaCenaVysledok | null>(null);

  // Otvorenie = načítanie uloženého stavu. Zavretie bez uloženia teda nič
  // nezmení a druhé otvorenie nezdedí rozrobené čísla.
  useEffect(() => {
    if (!open) return;
    setZisk(normalizeNum(varianta.zisk_pct));
    setSleva(normalizeNum(varianta.sleva_pct));
    setMarze(
      Object.fromEntries(
        zostavy.map((z) => [
          z.id,
          z.zisk_pct_override == null ? "" : normalizeNum(z.zisk_pct_override),
        ]),
      ),
    );
    setCena("");
    setCiel(null);
    // Načítať len pri otvorení — inak by refetch zákazky prepísal to, čo
    // má user rozrobené pod rukami.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Hypotéza pre backend — posiela sa len to, čo sa dá prečítať ako číslo. */
  const hypoteza = useMemo(() => {
    const cislo = (v: string): string | null => {
      const p = parseDecimal(v);
      return p !== "" && Number.isFinite(Number.parseFloat(p)) ? p : null;
    };
    return {
      zisk_pct: cislo(zisk),
      sleva_pct: cislo(sleva),
      zostavy: zostavy.map((z) => ({
        zostava_id: z.id,
        zisk_pct_override: cislo(marze[z.id] ?? ""),
      })),
    };
  }, [zisk, sleva, marze, zostavy]);

  // Debounce až na hypotéze: pri písaní „1" → „18" → „185" sa nepošlú tri
  // dotazy a hypotéza v queryKey zaručí, že vyhrá tá k poslednému vstupu.
  const hypotezaDebounced = useDebounced(hypoteza, 300);
  const nahlad = useCenaNahlad(varianta.id, hypotezaDebounced, open);
  const data = nahlad.data?.data;

  const dopocitaj = useCielovaCena(varianta.id);
  const ulozMarze = useUlozMarze(zakazkaId, varianta.id);

  const zmenene = useMemo(
    () =>
      zostavy
        .filter((z) => {
          const povodne =
            z.zisk_pct_override == null
              ? ""
              : normalizeNum(z.zisk_pct_override);
          return (marze[z.id] ?? "") !== povodne;
        })
        .map<MarzaZostavy>((z) => ({
          zostava_id: z.id,
          kod: z.kod,
          zisk_pct_override: parseDecimal(marze[z.id] ?? "") || null,
        })),
    [zostavy, marze],
  );

  const maZmeny =
    zmenene.length > 0 ||
    parseDecimal(zisk) !== normalizeNum(varianta.zisk_pct) ||
    parseDecimal(sleva) !== normalizeNum(varianta.sleva_pct);

  async function spocitajCiel(): Promise<void> {
    const p = parseDecimal(cena);
    if (!p || !(Number.parseFloat(p) > 0)) return;
    try {
      const out = await dopocitaj.mutateAsync({
        cena: p,
        s_dph: sDph,
        // Rada musí platiť pre rozrobený stav, nie pre uložený
        sleva_pct: hypoteza.sleva_pct,
        zostavy: hypoteza.zostavy,
      });
      setCiel(out.data);
    } catch (e) {
      toast({
        title: "Prepočet zlyhal",
        description: extractErrorMessage(e),
        variant: "destructive",
      });
    }
  }

  async function uloz(): Promise<void> {
    try {
      const vysledok = await ulozMarze.mutateAsync({
        zisk_pct: parseDecimal(zisk),
        sleva_pct: parseDecimal(sleva),
        zostavy: zmenene,
        predosle: {
          zisk_pct: normalizeNum(varianta.zisk_pct),
          sleva_pct: normalizeNum(varianta.sleva_pct),
          zostavy: zmenene.map((z) => {
            const orig = zostavy.find((x) => x.id === z.zostava_id);
            return {
              zostava_id: z.zostava_id,
              kod: z.kod,
              zisk_pct_override:
                orig?.zisk_pct_override == null
                  ? null
                  : normalizeNum(orig.zisk_pct_override),
            };
          }),
        },
      });
      if (vysledok.chyby.length > 0) {
        // Nie je to atomické — dialóg zostáva otvorený a vypíše, čo neprešlo
        toast({
          title: `Uložené čiastočne (${vysledok.hotovo})`,
          description: `Neprešli: ${vysledok.chyby
            .map((c) => c.kod)
            .join(", ")}`,
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Marže uložené" });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Uloženie zlyhalo",
        description: extractErrorMessage(e),
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Ceny a marže</DialogTitle>
          <DialogDescription className="sr-only">
            Zisk, zľava a ceny jednotlivých zostav na jednom mieste.
          </DialogDescription>
        </DialogHeader>

        {/* 1. Cieľová cena — pomôcka, ktorá len predvyplní globálny zisk */}
        <section className="space-y-2 rounded-lg border bg-background-alt/40 p-3">
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="ciel-cena" className="text-xs">
                Cieľová cena (€)
              </Label>
              <Input
                id="ciel-cena"
                value={cena}
                onChange={(e) => {
                  setCena(e.target.value);
                  setCiel(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void spocitajCiel();
                }}
                placeholder="19 998,50"
                inputMode="decimal"
                className="h-9 w-40 font-mono"
              />
            </div>
            <div className="flex gap-1 pb-1">
              {(
                [
                  [true, "s DPH"],
                  [false, "bez DPH"],
                ] as const
              ).map(([hodnota, label]) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={sDph === hodnota}
                  onClick={() => {
                    setSDph(hodnota);
                    setCiel(null);
                  }}
                  className={
                    sDph === hodnota
                      ? "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                      : "rounded-md border px-2.5 py-1 text-xs text-muted-foreground hover:bg-background"
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              className="mb-1"
              disabled={!parseDecimal(cena) || dopocitaj.isPending}
              onClick={() => void spocitajCiel()}
              title="Dopočítaj zisk %, ktorý dá zadanú cenu"
            >
              <Calculator className="h-4 w-4" />
              Prepočítaj
            </Button>
          </div>

          {ciel ? (
            ciel.dosiahnutelne && ciel.zisk_pct ? (
              <div
                className="flex items-center gap-3 text-sm"
                data-testid="ciel-vysledok"
              >
                <span>
                  Zisk <b className="font-mono">{formatPct(ciel.zisk_pct)}</b> →{" "}
                  <span className="font-mono">
                    {formatEur(ciel.cena_s_dph)}
                  </span>{" "}
                  s DPH
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setZisk(normalizeNum(ciel.zisk_pct!))}
                  title="Predvyplní globálny zisk nižšie — uloží sa až tlačidlom Uložiť"
                >
                  <Target className="h-3.5 w-3.5" />
                  Použiť do formulára
                </Button>
              </div>
            ) : (
              <p
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="ciel-dovod"
              >
                {ciel.dovod}
              </p>
            )
          ) : null}
        </section>

        {/* 2. Globálne páky */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="m-zisk" className="text-xs">
              Zisk na celý výpočet (%)
            </Label>
            <Input
              id="m-zisk"
              value={zisk}
              onChange={(e) => setZisk(e.target.value)}
              inputMode="decimal"
              className="h-9 w-28 font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="m-sleva" className="text-xs">
              Sleva (%)
            </Label>
            <Input
              id="m-sleva"
              value={sleva}
              onChange={(e) => setSleva(e.target.value)}
              disabled={!prava.podmienky}
              title={prava.podmienky ? undefined : VETA.podmienky}
              inputMode="decimal"
              className="h-9 w-28 font-mono"
            />
          </div>
          <p className="pb-2 text-xs text-muted-foreground">
            DPH {formatPct(varianta.dph_pct)} — sadzba je daná zákonom, tu sa nemení.
          </p>
        </div>

        {/* 3. Zostavy s ich percentami */}
        <div className="max-h-[45vh] overflow-y-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[220px]">Zostava</TableHead>
                <TableHead className="w-14 text-right">ks</TableHead>
                <TableHead className="text-right">Náklad / ks</TableHead>
                <TableHead className="w-[150px]">Zisk %</TableHead>
                <TableHead className="text-right">Cena celkom</TableHead>
                <TableHead className="w-16 text-right">Podiel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zostavy.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    Výpočet nemá žiadne zostavy — nie je čo oceniť.
                  </TableCell>
                </TableRow>
              ) : (
                zostavy.map((z) => (
                  <MarzaRiadok
                    key={z.id}
                    zostava={z}
                    hodnota={marze[z.id] ?? ""}
                    globalnyZisk={zisk}
                    riadok={data?.zostavy.find((r) => r.zostava_id === z.id)}
                    onChange={(v) =>
                      setMarze((m) => ({ ...m, [z.id]: v }))
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* 4. Živé súčty + uloženie */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <div className="flex gap-5 text-sm" data-testid="marze-sucty">
            <Suma label="Bez DPH" hodnota={data?.cena_bez_dph} />
            <Suma label="DPH" hodnota={data?.dph} />
            <Suma label="S DPH" hodnota={data?.cena_s_dph} silne />
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Zavrieť
            </Button>
            <Button
              disabled={!maZmeny || ulozMarze.isPending}
              onClick={() => void uloz()}
              title={
                maZmeny
                  ? "Uloží globálny zisk, slevu a zmenené marže zostáv"
                  : "Nič sa nezmenilo"
              }
            >
              Uložiť
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Suma({
  label,
  hodnota,
  silne,
}: {
  label: string;
  hodnota?: string | null;
  silne?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      <span
        className={
          silne
            ? "font-mono text-base font-semibold text-primary"
            : "font-mono text-sm"
        }
      >
        {hodnota == null ? "—" : formatEur(hodnota)}
      </span>
    </div>
  );
}

function MarzaRiadok({
  zostava,
  hodnota,
  globalnyZisk,
  riadok,
  onChange,
}: {
  zostava: Zostava;
  hodnota: string;
  globalnyZisk: string;
  riadok?: ZostavaMarzaRow;
  onChange: (v: string) => void;
}): JSX.Element {
  const zdedena = hodnota.trim() === "";
  return (
    <TableRow data-testid={`marza-${zostava.kod}`}>
      <TableCell className="text-sm">
        <span className="font-mono">{zostava.kod}</span>
        <span className="ml-2 text-muted-foreground">{zostava.nazov}</span>
        {/* Zľava zostavy (KOLO 227) — cena v riadku je už po nej */}
        {riadok?.zlava_pct != null && Number(riadok.zlava_pct) !== 0 ? (
          <span className="ml-2 rounded bg-amber-100 px-1 text-[10px] text-amber-900">
            zľava −{formatCisloBezNul(riadok.zlava_pct)} %
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {/* Zostava v metroch (KOLO 227) — cena celkom ráta z metrov */}
        {riadok?.mnozstvo_m != null
          ? `${formatCisloBezNul(riadok.mnozstvo_m)} m`
          : (riadok?.pocet_ks ?? zostava.pocet_ks)}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {riadok ? formatEur(riadok.zaklad) : "—"}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Input
            value={hodnota}
            onChange={(e) => onChange(e.target.value)}
            // Prázdne = zdedí globál, preto je v placeholderi jeho hodnota
            placeholder={globalnyZisk || "—"}
            inputMode="decimal"
            aria-label={`Zisk % zostavy ${zostava.kod}`}
            className="h-8 w-20 font-mono"
          />
          <span className="text-[10px] uppercase text-muted-foreground">
            {zdedena ? "zdedená" : "vlastná"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {riadok ? formatEur(riadok.cena_celkom) : "—"}
      </TableCell>
      <TableCell className="text-right font-mono text-xs text-muted-foreground">
        {riadok ? formatPct(riadok.podiel_pct) : "—"}
      </TableCell>
    </TableRow>
  );
}
